/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Box, Theme } from "@mui/material";
import { environment } from "../../environment";
import "./SpinnerRotate.scss";

const MIN_SIZE = 48;
const IST_BUCHUNGSPORTAL = environment.istBuchungsportal;

export type SpinnerProps = {
    color?: string;
    size?: number;
}

function outerSize(props: SpinnerProps) {
    return Math.floor(Math.max(props.size ?? MIN_SIZE, MIN_SIZE));
}

function ringWidth(props: SpinnerProps) {
    return outerSize(props) / (MIN_SIZE / 4);
}

function innerSize(props: SpinnerProps) {
    return outerSize(props) - 2 * ringWidth(props);
}

function color(props: SpinnerProps, theme: Theme) {
    return props.color ?? IST_BUCHUNGSPORTAL ? theme.palette.primary.main : theme.palette.secondary.main;
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>((props, ref) => {
    return (
        <Box
            ref={ref}
            sx={{
                display: "inline-block",
                position: "relative",
                width: outerSize(props),
                height: outerSize(props),
                transform: "translate(-50%, -50%)",
                "& div": {
                    boxSizing: "border-box",
                    display: "block",
                    position: "absolute",
                    width: innerSize(props),
                    height: innerSize(props),
                    margin: ringWidth(props),
                    borderWidth: ringWidth(props),
                    borderStyle: "solid",
                    borderColor: (theme) => `${color(props, theme)} transparent transparent transparent`,
                    borderRadius: "50%",
                    animation: "lan-spinner-rotate 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite",
                },
                "& div:nth-child(1)": {
                    animationDelay: "-0.45s",
                },
                "& div:nth-child(2)": {
                    animationDelay: "-0.30s",
                },
                "& div:nth-child(3)": {
                    animationDelay: "-0.15s",
                },
            }}
            role="alert"
            aria-busy="true"
        >
            <div></div>
            <div></div>
            <div></div>
            <div></div>
        </Box>
    )
});

export default Spinner;